import { useForm } from 'react-hook-form';
import gYupResolver from '../../../technical/form/gYupResolver.ts';
import * as yup from 'yup';
import { Stack } from '@mui/joy';
import { FormPredefinedDateRangeInput } from '../../../technical/form/FormPredefinedDateRangeInput.tsx';
import type { PredefinedRangeLabel } from '../../../predefinedDateRanges.ts';
import { defaultRowSpacing } from '../../../StackSpacing.ts';
import FormRadioGroup from '../../../technical/form/FormRadioGroup.tsx';
import type { ExposureDataType } from './ExposureChart.tsx';
import { updateWidgetState, useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import { calculateDefaultPresetRange } from 'components/management/TilesDateRangeSelector.tsx';
import WidgetSettings from 'components/management/customDashboard/WidgetSettings.tsx';
import { exposureRadioOptions } from './ExposureChartWrapper.tsx';
import { FormPortfolioFilters } from 'components/technical/form/FormPortfolioFilters.tsx';
import {
  type PortfolioFilters,
  portfolioFiltersSchema,
} from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';

const schema = yup
  .object({
    title: yup.string().required(),
    predefinedDateRange: yup.object().required(),
    dataType: yup.string().required(),
  })
  .concat(portfolioFiltersSchema);

interface FormState extends Partial<PortfolioFilters> {
  title: string;
  predefinedDateRange: {
    label: PredefinedRangeLabel;
  };
  dataType: ExposureDataType;
}

export interface PortfolioExposureState {
  title: string;
  predefinedDateRange?: PredefinedRangeLabel;
  dataType: ExposureDataType;
}

export const PortfolioExposureWidgetSettings = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}) => {
  const initialState = useWidgetState<PortfolioExposureState>(id);
  const updater = updateWidgetState<PortfolioExposureState>();

  const methods = useForm<FormState>({
    resolver: gYupResolver(schema),
    defaultValues: {
      ...initialState,
      predefinedDateRange: calculateDefaultPresetRange(initialState.predefinedDateRange),
    },
  });

  const handleFormSubmit = async (input: FormState): Promise<void> => {
    await updater({
      id,
      state: {
        ...input,
        title: input.title,
        predefinedDateRange: input.predefinedDateRange.label,
        dataType: input.dataType,
      },
      updateBackend: true,
    });

    onFinishedEditing();
  };

  return (
    <WidgetSettings methods={methods} handleFormSubmit={handleFormSubmit} onRemovedWidget={onRemovedWidget}>
      <Stack width={'100%'} rowGap={defaultRowSpacing}>
        <FormPredefinedDateRangeInput name={'predefinedDateRange'} label={'Date range'} />
        <FormPortfolioFilters />
        <FormRadioGroup name={'dataType'} width="minContent" options={exposureRadioOptions} />
      </Stack>
    </WidgetSettings>
  );
};
